/* Originals display-only bridge to the accepted ROBO360 renderers.
 * Never changes selected variants, offers, cart properties or source artwork. */
(function () {
  'use strict';
  const profiles = {
    IP16P: ['16', 'iphone16'], IP16PM: ['16', 'iphone16'],
    IP17P: ['17', 'iphone-17-pro'], IP17PM: ['17', 'iphone-17-pro-max'],
    // Same owner-approved aliases as the customizer and Originals galleries.
    IP18P: ['17', 'iphone-17-pro'], IP18PM: ['17', 'iphone-17-pro-max']
  };
  const loads = new Map();
  const prefetched = new Set();
  const yieldToPage = () => new Promise(resolve => setTimeout(resolve, 0));
  function renderer(key, url) {
    const factory = () => key === 'iphone16' ? window.RoboPixel360Renderer : window.RoboPixel360Renderers?.[key];
    if (factory()) return Promise.resolve(factory());
    if (!loads.has(url)) loads.set(url, new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const fail = () => { clearTimeout(timeout); script.remove(); reject(Error('Renderer unavailable')); };
      const timeout = setTimeout(fail, 15000);
      script.async = true; script.src = url;
      script.onload = () => { clearTimeout(timeout); factory() ? resolve(factory()) : fail(); };
      script.onerror = fail;
      document.head.appendChild(script);
    }).catch(error => { loads.delete(url); throw error; }));
    return loads.get(url);
  }
  async function loadImage(src) {
    const img = new Image(); img.crossOrigin = 'anonymous'; img.src = src;
    let timeout;
    try { await Promise.race([img.decode(), new Promise((_, reject) => { timeout = setTimeout(() => reject(Error('Artwork unavailable')), 15000); })]); }
    finally { clearTimeout(timeout); }
    return img;
  }
  async function artwork(src, crop, bleed) {
    const [img, photo] = await Promise.all([loadImage(src), bleed?.photo ? loadImage(bleed.photo) : null]);
    await yieldToPage();
    const [x, y, w, h] = crop;
    const canvas = document.createElement('canvas'); canvas.width = 768; canvas.height = Math.round(768 * h * img.naturalHeight / (w * img.naturalWidth));
    const ctx = canvas.getContext('2d'), iw = img.naturalWidth, ih = img.naturalHeight;
    // Existing product photos supply only bleed missing from the design file.
    // Do not invent or stretch artwork into those areas. Camera/TPU remain masked
    // by the renderer; the original design supplies the main printable panel.
    if (photo) {
      const [left, top, right, bottom] = bleed.panel;
      ctx.drawImage(photo, left * photo.naturalWidth, top * photo.naturalHeight,
        (right - left) * photo.naturalWidth, (bottom - top) * photo.naturalHeight, 0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, -x / w * canvas.width, -y / h * canvas.height, canvas.width / w, canvas.height / h);
    ctx.getImageData(0, 0, 1, 1);
    return canvas;
  }
  if (customElements.get('originals-360')) return;
  customElements.define('originals-360', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.root = this.closest('readymade-product-v2');
      if (!this.root) return;
      try { this.config = JSON.parse(this.querySelector('[data-originals-360-config]').textContent); } catch (_) { return; }
      this.events = new AbortController(); this.revision = 0; this.visible = true;
      this.nav = this.querySelector('[data-360-modes]'); this.stage = this.querySelector('[data-360-stage]');
      this.status = this.querySelector('[data-360-status]'); this.turns = this.querySelector('[data-360-turns]');
      this.flatButton = this.querySelector('[data-360-flat]'); this.button = this.querySelector('[data-360-open]');
      this.autoButton = this.querySelector('[data-360-auto]'); this.slider = this.querySelector('[data-360-angle]');
      const on = (target, name, fn) => target.addEventListener(name, fn, { signal: this.events.signal });
      on(this.flatButton, 'click', () => this.flat());
      on(this.button, 'click', () => this.show());
      on(this.button, 'pointerenter', () => this.prefetch());
      on(this.button, 'focus', () => this.prefetch());
      on(this.root, 'robopixel:readymade-gallery', () => this.sync());
      on(this.root, 'click', e => { if (e.target.closest('[data-rpv2-thumb]')) this.flat(); });
      on(this.slider, 'input', () => this.viewer?.setAngle(Number(this.slider.value)));
      on(this.autoButton, 'click', () => this.viewer?.setAutoRotate(!this.viewer.getState().auto));
      on(this.querySelector('[data-360-reset]'), 'click', () => this.viewer?.reset());
      on(document, 'visibilitychange', () => this.activity());
      on(window, 'pagehide', () => { this.flat(); this.release(); });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.activity(); });
        this.observer.observe(this.root.querySelector('.rpv2-gallery'));
      }
      customElements.whenDefined('readymade-product-v2').then(() => { if (this.isConnected) this.sync(); });
    }
    sync() {
      const variant = this.root.variants?.[this.root.selectedId];
      const id = this.root.selectedId;
      if (id !== this.variantId) { this.flat(); this.release(); this.variantId = id; }
      this.profile = variant && profiles[variant.suffix];
      this.nav.hidden = !(this.profile && this.config.crops?.[this.profile[0]]);
      this.model = variant?.title || '';
    }
    prefetch() {
      // Speculative downloads only after both initial page and hero have loaded,
      // and only on explicit hover/focus. Never build or run a hidden WebGL view.
      const connection = navigator.connection;
      const hero = this.root.querySelector('[data-rpv2-slide="0"] img');
      if (!this.profile || this.nav.hidden || this.active || this.dataset.state === 'loading' || document.readyState !== 'complete' ||
          !hero?.complete || !hero.naturalWidth || !this.visible || document.hidden ||
          connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')) return;
      const [group, key] = this.profile, assets = this.config.models[key];
      for (const url of [assets.renderer, this.config.artwork, this.config.bleed?.[group]?.photo, ...assets.photos].filter(Boolean)) {
        if (prefetched.has(url)) continue;
        prefetched.add(url);
        const link = document.createElement('link'); link.rel = 'prefetch'; link.href = url; link.fetchPriority = 'low';
        document.head.appendChild(link);
      }
    }
    message(key) {
      this.status.textContent = this.config.copy[key] || ''; this.status.hidden = !key;
      this.dataset.state = key || (this.active ? 'active' : 'photos');
      this.button.setAttribute('aria-busy', String(key === 'loading'));
    }
    activity() { this.viewer?.setActive(!!this.active && this.visible && !document.hidden); }
    flat() {
      ++this.revision; this.active = false; this.button.disabled = false;
      this.stage.hidden = true; this.turns.hidden = true;
      const main = this.root.querySelector('[data-rpv2-gallery-main]'); if (main) main.hidden = false;
      this.flatButton.setAttribute('aria-pressed', 'true'); this.button.setAttribute('aria-pressed', 'false');
      this.message(''); this.activity();
    }
    release() {
      const old = this.viewer; this.viewer = null; old?.dispose();
      this.canvas?.remove(); this.canvas = null;
    }
    controls() {
      if (!this.viewer) return;
      const state = this.viewer.getState();
      this.slider.value = Math.round(state.angle); this.slider.setAttribute('aria-valuetext', this.slider.value + '°');
      this.autoButton.setAttribute('aria-pressed', String(state.auto));
      this.autoButton.textContent = this.config.copy[state.auto ? 'stop' : 'start'];
    }
    async show() {
      if (!this.profile || this.nav.hidden) return;
      const ticket = ++this.revision, [group, key] = this.profile, model = this.model;
      this.message('loading'); this.button.disabled = true;
      let candidate, target;
      try {
        if (!this.viewer) {
          const assets = this.config.models[key];
          const [factory, texture] = await Promise.all([renderer(key, assets.renderer), artwork(this.config.artwork, this.config.crops[group], this.config.bleed?.[group])]);
          if (ticket !== this.revision || !this.isConnected) return;
          target = document.createElement('canvas'); target.tabIndex = 0;
          target.setAttribute('aria-label', model + '. ' + this.config.copy.hint); target.dataset.model = model;
          target.addEventListener('robopixel:360-change', () => this.controls());
          target.addEventListener('robopixel:360-error', () => {
            if (this.canvas !== target) return;
            this.flat(); this.release(); this.message('error');
          });
          await yieldToPage();
          if (ticket !== this.revision || !this.isConnected) return;
          candidate = await factory.create(target, assets.photos);
          if (ticket !== this.revision || !this.isConnected) { candidate.dispose(); return; }
          candidate.setArtwork(texture); this.viewer = candidate; this.canvas = target; this.stage.prepend(target);
        }
        this.active = true; this.stage.hidden = false; this.turns.hidden = false;
        this.root.querySelector('[data-rpv2-gallery-main]').hidden = true;
        this.flatButton.setAttribute('aria-pressed', 'false'); this.button.setAttribute('aria-pressed', 'true');
        this.viewer.setAutoRotate(!window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        this.activity(); this.controls(); this.message('');
      } catch (error) {
        candidate?.dispose();
        if (ticket !== this.revision) return;
        this.flat(); this.release(); this.message('error');
        console.warn('[Originals 360]', error.message);
      } finally { if (ticket === this.revision) this.button.disabled = false; }
    }
    disconnectedCallback() {
      ++this.revision; this.release(); this.observer?.disconnect(); this.events?.abort(); this.events = null;
    }
  });
})();
